<template>
  <div class="trezo-card bg-white dark:bg-[#0c1427] mb-[25px] p-[20px] md:p-[25px] rounded-md">
    <div class="trezo-card-content relative">
        <span class="block">
            {{ directT('dashboard.totalOrders') }}
        </span>
        <h5 class="mb-0 mt-[3px] text-[20px] pb-[15px]" v-if="loading">
            {{ directT('dashboard.crm.loading') }}
        </h5>
        <h5 class="mb-0 mt-[10px] text-[20px] pb-[15px]" v-else>
            {{ formatCurrency(totalAmount, currencyCode) }}
        </h5>
        <div class="absolute -top-[28px] ltr:-right-[9px] rtl:-left-[9px] max-w-[120px]">
          <apexchart
          v-if="isClient && dailyOrders.length > 0"
          type="bar"
          height="110"
          :options="totalOrders"
          :series="orders"
        ></apexchart>
        </div>
        <div class="mt-[15px] md:mt-[24px] flex items-center justify-end">
            <span class="block text-sm">
                {{ directT('dashboard.last30Days') }}
            </span>
        </div>
    </div>
</div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed, onUnmounted } from "vue";
import { supabase } from "@/lib/supabaseClient";
import { getCurrentOrgId } from "@/utils/getCurrentOrgId";
import { formatCurrency, getUserCurrency } from "@/utils/currency";
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";

// Type definition for nested translation objects
type TranslationObject = { [key: string]: string | TranslationObject };

// Define interfaces for type safety
interface Deal {
  dealamount: number | null;
  dealtime: string | null;
}

export default defineComponent({
  name: "TotalOrdersChart",
  props: {
    currentUserId: {
      type: String,
      default: null
    },
    userPermissions: {
      type: Object,
      default: () => ({
        is_owner: false,
        edit_all_leads: false,
        view_all_leads: false,
        can_invite_users: false
      })
    }
  },
  setup(props) {
    const isClient = ref(false);
    const loading = ref(true);
    const totalAmount = ref(0);
    const dailyOrders = ref<number[]>([]);
    const organizationId = ref<string | null>(null);
    const refreshTimer = ref<number | null>(null);
    const currencyCode = ref('ILS'); // ברירת מחדל
    
    // i18n setup
    const { locale } = useI18n();
    
    // Store direct references to translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string) => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return enResult;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return result;
    };
    
    const orders = computed(() => [
      {
        name: directT('dashboard.totalOrders'),
        data: dailyOrders.value,
      },
    ]);
    
    const totalOrders = ref({
      chart: {
        type: "bar",
        height: 110,
        toolbar: {
          show: false,
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350
          }
        }
      },
      plotOptions: {
        bar: {
          columnWidth: "250%",
          borderRadius: 2
        },
      },
      colors: ["#0dcaf0"],
      dataLabels: {
        enabled: false,
      },
      grid: {
        show: false,
        borderColor: "#ECEEF2",
      },
      stroke: {
        width: 2,
        show: true,
        colors: ["transparent"],
      },
      xaxis: {
        categories: [] as string[],
        axisTicks: {
          show: false,
          color: "#ECEEF2",
        },
        axisBorder: {
          show: false,
          color: "#ECEEF2",
        },
        labels: {
          show: false,
          style: {
            colors: "#8695AA",
            fontSize: "12px",
          },
        },
      },
      yaxis: {
        show: false,
        labels: {
          style: {
            colors: "#64748B",
            fontSize: "12px",
          },
        },
        axisBorder: {
          show: false,
          color: "#ECEEF2",
        },
        axisTicks: {
          show: false,
          color: "#ECEEF2",
        },
      },
      tooltip: {
        y: {
          formatter: function (val: string) {
            return formatCurrency(Number(val), currencyCode.value);
          },
        },
      },
    });

    // Format amount with commas
    const formatAmount = (amount: number): string => {
      return amount.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    };
    
    // Get date from X days ago in ISO format
    const getDateXDaysAgo = (days: number): string => {
      const date = new Date();
      date.setDate(date.getDate() - days);
      return date.toISOString();
    };
    
    // Get the organization's currency
    const getCurrency = async () => {
      try {
        currencyCode.value = await getUserCurrency();
      } catch (error) {
        console.error('Error fetching currency:', error);
      }
    };
    
    // Fetch total orders/deals for the last 30 days
    const fetchTotalOrders = async () => {
      try {
        loading.value = true;
        organizationId.value = await getCurrentOrgId();
        
        if (!organizationId.value) {
          console.error("User is not associated with an organization");
          loading.value = false;
          return;
        }
        
        const thirtyDaysAgo = getDateXDaysAgo(30);
        
        // Build query based on permissions
        let query = supabase
          .from('leads')
          .select('dealamount, dealtime')
          .eq('org_id', organizationId.value)
          .not('dealtime', 'is', null)
          .not('dealamount', 'is', null)
          .gte('dealtime', thirtyDaysAgo);
          
        // If user can't view all leads, only show assigned leads
        if (props.currentUserId && !props.userPermissions.is_owner && !props.userPermissions.view_all_leads) {
          query = query.eq('assigned_to', props.currentUserId);
        }
        
        // Execute the query
        const { data: recentDeals, error } = await query;
        
        if (error) throw error;
        
        // Calculate total amount
        let total = 0;
        if (recentDeals && recentDeals.length > 0) {
          total = (recentDeals as Deal[]).reduce((sum, deal) => {
            const amount = typeof deal.dealamount === 'number' ? deal.dealamount : 0;
            return sum + amount;
          }, 0);
        }
        
        console.log(`Total Orders: Current amount - $${total}`);
        totalAmount.value = total;
        
        // Get daily data for the chart
        await fetchDailyData(thirtyDaysAgo);
        
      } catch (error) {
        console.error('Error fetching total orders:', error);
      } finally {
        loading.value = false;
      }
    };
    
    // Fetch daily order data for the chart
    const fetchDailyData = async (startDate: string) => {
      try {
        // Create an array of 30 days with 0 values
        const dailyAmounts = new Array(30).fill(0);
        
        // Generate date labels for the last 30 days
        const today = new Date();
        const dateLabels: string[] = [];
        
        for (let i = 29; i >= 0; i--) {
          const d = new Date();
          d.setDate(today.getDate() - i);
          dateLabels.push((d.getMonth() + 1) + "/" + d.getDate());
        }
        
        // Update chart x-axis categories
        totalOrders.value.xaxis.categories = dateLabels;
        
        // Build query based on permissions
        let query = supabase
          .from('leads')
          .select('dealamount, dealtime')
          .eq('org_id', organizationId.value)
          .not('dealtime', 'is', null)
          .not('dealamount', 'is', null)
          .gte('dealtime', startDate);
          
        // If user can't view all leads, only show assigned leads
        if (props.currentUserId && !props.userPermissions.is_owner && !props.userPermissions.view_all_leads) {
          query = query.eq('assigned_to', props.currentUserId);
        }
        
        // Execute the query
        const { data: deals, error } = await query;
        
        if (error) throw error;
        
        if (deals && deals.length > 0) {
          // Process each deal
          (deals as Deal[]).forEach(deal => {
            if (deal.dealtime && deal.dealamount) {
              const dealDate = new Date(deal.dealtime);
              // Calculate days difference from today
              const diffTime = today.getTime() - dealDate.getTime();
              const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
              
              // If deal is within last 30 days
              if (diffDays >= 0 && diffDays < 30) {
                // Add amount to the corresponding day
                const index = 29 - diffDays; // Reverse index (29 is today, 0 is 30 days ago)
                dailyAmounts[index] += Number(deal.dealamount);
              }
            }
          });
          
          // Log daily amounts
          console.log('Total Orders: Daily amounts data updated');
        }
        
        // Update chart data
        dailyOrders.value = dailyAmounts;
      } catch (error) {
        console.error('Error fetching daily order data:', error);
      }
    };
    
    // Start a timer to refresh data periodically
    const startRefreshTimer = () => {
      // Refresh every 30 seconds
      refreshTimer.value = window.setInterval(() => {
        console.log('Refreshing Total Orders data...');
        fetchTotalOrders();
      }, 30000); // 30 seconds
    };
    
    onMounted(async () => {
      isClient.value = true;
      await getCurrency();
      fetchTotalOrders();
      startRefreshTimer();
    });
    
    onUnmounted(() => {
      // Clear the refresh timer when component is unmounted
      if (refreshTimer.value) {
        clearInterval(refreshTimer.value);
      }
    });

    return {
      isClient,
      loading,
      totalAmount,
      orders,
      totalOrders,
      dailyOrders,
      formatAmount,
      formatCurrency,
      currencyCode,
      directT,
    };
  },
});
</script>
